<template>
  <div class="comparator">
    <el-card class="comparator__card">
      <h3>{{ $t('home.comparator.title') }}</h3>
      <component :is="composentLoaderText"
                 :main-text="getTextIfExists('')"
                 :is-commissionnaire="isCommissionaire"
                 :link-text="getTextIfExists('Link')"
                 :link-path="getTextIfExists('LinkPath', '/comparator')"
                 :button-text="getTextIfExists('Button')"
                 :button-action="getTextIfExists('ButtonAction')" />
      <div
        class="comparator__offers">
        <div
          class="comparator__offers__offer"
          v-for="(offer, index) in offersToDisplay"
          :key="index">
          <router-link class="comparator__offers__offer__href" :to="{ name: 'comparator' }">
            <comparator-offer :offer="offer"
                              :hide-price="hidePriceInfo"
                              class="offer-card__offer"/>
          </router-link>
        </div>
      </div>
      <div class="comparator__actions" v-if="offers.length">
        <router-link :to="{ name: 'comparator' }">
          <el-button type="success" class="comparator__actions__btn">
            {{ $tc('home.comparator.displayQuotation', offersToDisplay.length ) }}
          </el-button>
        </router-link>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ComparatorOffer from '@/components/home/ComparatorOffer.vue';
import offersHelper from '@/utils/helpers/offers';
import MoveManager from '../../utils/components/MoveManager.vue';

export default {
  name: 'comparator',
  extends: MoveManager,
  components: {
    ComparatorOffer,
  },
  computed: {
    ...mapGetters({
      offers: 'offerStore/offers',
      demand: 'demandStore/demand',
      projectSelected: 'projectStore/projectSelected',
    }),
    isWaitingState() {
      return !!offersHelper.getWaitingPrebookedOffers(this.offers).length;
    },
    isFirstState() {
      return !!offersHelper.getPrebookedOffers(this.offers).length;
    },
    isSecondState() {
      return !!this.validOffer;
    },
    needsNewSignatureState() {
      return offersHelper.needsNewSignature(this.offers);
    },
    stepTextPath() {
      const clientType = (this.isIndividual && 'individual')
        || (this.isMinarm && 'minarm')
        || 'commissionnaire';
      // Order is important here => isFirstState is always true when isWaitingState is also true
      const step = (this.needsNewSignatureState && 'stepReSign')
        || (this.isSecondState && 'step2')
        || (this.isWaitingState && 'step0')
        || (this.isFirstState && 'step1');
      return this.$te(`home.comparator.${clientType}.${step}`)
        ? `home.comparator.${clientType}.${step}` : '';
    },
    composentLoaderText() {
      const path = './comparator';
      if (!this.offers.length) {
        return () => import(`${path}/NoOffers.vue`);
      }
      if (this.isCommissionaire
        && (this.isFirstState || this.isSecondState || this.isWaitingState)) {
        return () => import(`${path}/CommissionnaireStep.vue`);
      }
      return () => import(`${path}/OffersDisplayed.vue`);
    },
    offersToDisplay() {
      if (this.isCommissionaire && (this.isFirstState || this.isSecondState)) {
        return this.offers.filter((offer) => !offersHelper.INITIAL_STATUS.includes(offer.status));
      }
      return this.offers;
    },
  },
  methods: {
    getTextIfExists(textPath, defaultValue) {
      const linkText = `${this.stepTextPath}${textPath}`;
      return this.$te(linkText) ? this.$t(linkText, {
        offerId: this.validOffer && this.validOffer.id,
        signPath: offersHelper.needsFirstSignature(this.offers)
          ? 'signature'
          : 'resume',
      }) : defaultValue;
    },
  },
};
</script>

<style scoped lang="scss">
.comparator {
  margin: 20px 0;
  @include breakpoint(tablet) {
    margin: 20px;
  }
  @include breakpoint(wide) {
    margin: 20px $--wide-margin;
  }
  @include breakpoint(wide-xl) {
    margin: 20px $--wide-xl-margin;
  }
  &__card {
    border-radius: 0;
    @include breakpoint(tablet) {
      border-radius: 4px;
    }
    :deep(&__panel) {
      border-radius: 4px;
      padding: 20px;
      background-color: $--color-panel;
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
      @include breakpoint(mobile) {
        flex-direction: row;
      }
      &__img {
        margin: 20px auto;
        width: 150px;
        @include breakpoint(mobile) {
          margin: auto 0 auto 20px;
        }
      }
      &__text {
        text-align: center;
        margin: auto 20px;
        flex: 1 1 auto;
        @include breakpoint(mobile) {
          text-align: left;
        }
      }
    }
  }
  &__offers {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-wrap: wrap;
    @include breakpoint(tablet) {
      flex-direction: row;
      max-height: 320px;
    }
    &__offer {
      flex: 1 1;
      margin-bottom: 20px;
      min-width: 220px;
      @include breakpoint(tablet) {
        margin-top: 20px;
        max-width: 300px;
        padding: 0 10px;
      }
      &__href {
        text-decoration: none;
      }
    }
  }
  &__actions {
    margin-top: 20px;
    text-align: center;
  }
}
</style>
